import type { ClientTemporaryMediaItem } from '@DigitaleDoerfer/digitale-doerfer-api';
import { useCallback, useMemo, useState } from 'react';

import { useDeleteTemporaryDocumentItem } from '@/features/api/hooks/shared-document/useDeleteTemporaryDocumentItem';
import { useDeleteTemporaryMediaItem } from '@/features/api/hooks/shared-media/useDeleteTemporaryMediaItem';
import { useMediaPicker } from '@/features/media/hooks/useMediaPicker';
import type {
  MediaItemPlaceholder,
  DocumentItemPlaceholder,
  ImagePickType,
  DocumentPickerResult
} from '@/features/media/types/picker';
import { isEmptyOrNull } from '@/shared/utils/string';

type UseMediaPlaceholdersOptions = {
  initialImages?: MediaItemPlaceholder[];
  initialDocuments?: DocumentItemPlaceholder[];
};

export const useMediaPlaceholders = ({
  initialImages = [],
  initialDocuments = []
}: UseMediaPlaceholdersOptions = {}) => {
  const {
    pickImage,
    pickDocument,
    isImageUploadPending,
    isDocumentUploadPending,
    attachmentFeatureValues
  } = useMediaPicker();

  // #region Image state
  const [mediaItemPlaceholders, setMediaItemPlaceholders] =
    useState<MediaItemPlaceholder[]>(initialImages);

  const { mutate: deleteTemporaryMediaItem } = useDeleteTemporaryMediaItem();

  const addImage = useCallback(
    (temporaryMediaItem: ClientTemporaryMediaItem, localUri: string) => {
      setMediaItemPlaceholders((prev) => [
        ...prev,
        {
          temporaryMediaItemId: temporaryMediaItem.id,
          mediaItem: temporaryMediaItem.mediaItem,
          localUri
        }
      ]);
    },
    []
  );

  const onPickImage = useCallback(
    async (pick: ImagePickType) => {
      const result = await pickImage(pick);
      // Without a temporary item the upload failed — nothing to add here.
      if (result?.temporaryMediaItem) {
        addImage(result.temporaryMediaItem, result.image.uri);
      }
    },
    [pickImage, addImage]
  );

  const onRemoveImage = useCallback(
    (item: MediaItemPlaceholder) => {
      setMediaItemPlaceholders((prev) => {
        const index = prev.indexOf(item);
        if (index === -1) return prev;
        const updated = [...prev];
        updated.splice(index, 1);
        return updated;
      });

      if (!isEmptyOrNull(item.temporaryMediaItemId)) {
        deleteTemporaryMediaItem(item.temporaryMediaItemId);
      }
    },
    [deleteTemporaryMediaItem]
  );

  const onReorderImages = useCallback((reordered: MediaItemPlaceholder[]) => {
    setMediaItemPlaceholders(reordered);
  }, []);

  const temporaryMediaItemIds = useMemo(
    () =>
      mediaItemPlaceholders
        .map(({ temporaryMediaItemId }) => temporaryMediaItemId)
        .filter((id): id is string => !isEmptyOrNull(id)),
    [mediaItemPlaceholders]
  );
  // #endregion

  // #region Document state
  const [documentItemPlaceholders, setDocumentItemPlaceholders] =
    useState<DocumentItemPlaceholder[]>(initialDocuments);

  const { mutate: deleteTemporaryDocumentItem } =
    useDeleteTemporaryDocumentItem();

  const addDocument = useCallback((result: DocumentPickerResult) => {
    setDocumentItemPlaceholders((prev) => [
      ...prev,
      {
        temporaryDocumentItemId: result.temporaryDocumentItem.id,
        documentItem: result.temporaryDocumentItem.documentItem
      }
    ]);
  }, []);

  const onPickDocument = useCallback(async () => {
    const result = await pickDocument();
    if (result) addDocument(result);
  }, [pickDocument, addDocument]);

  const onRemoveDocument = useCallback(
    (item: DocumentItemPlaceholder) => {
      setDocumentItemPlaceholders((prev) => {
        const index = prev.indexOf(item);
        if (index === -1) return prev;
        const updated = [...prev];
        updated.splice(index, 1);
        return updated;
      });

      if (!isEmptyOrNull(item.temporaryDocumentItemId)) {
        deleteTemporaryDocumentItem(item.temporaryDocumentItemId);
      }
    },
    [deleteTemporaryDocumentItem]
  );

  const onReorderDocuments = useCallback(
    (reordered: DocumentItemPlaceholder[]) => {
      setDocumentItemPlaceholders(reordered);
    },
    []
  );

  const temporaryDocumentItemIds = useMemo(
    () =>
      documentItemPlaceholders
        .map(({ temporaryDocumentItemId }) => temporaryDocumentItemId)
        .filter((id): id is string => !isEmptyOrNull(id)),
    [documentItemPlaceholders]
  );

  // #endregion

  return {
    // Pending states
    isImageUploadPending,
    isDocumentUploadPending,
    isUploadPending: isImageUploadPending || isDocumentUploadPending,
    // Image
    mediaItemPlaceholders,
    temporaryMediaItemIds,
    onPickImage,
    onRemoveImage,
    onReorderImages,
    // Document
    documentItemPlaceholders,
    temporaryDocumentItemIds,
    onPickDocument,
    onRemoveDocument,
    onReorderDocuments,
    // Feature
    attachmentFeatureValues
  };
};
